import type { ShopOffer } from '@shefira/shared';

import { closeButton } from './hud.js';
import { legalUrl } from './legal-links.js';
import { uiFormat, uiPrice, uiText } from './text.js';
import './shop-modals.css';

/**
 * The step before paying (ADR 0044): what is bought, for how much, and the
 * box the buyer ticks to ask for immediate delivery and waive the right of
 * withdrawal (code de la consommation L221-28 13°). « Payer » stays grey
 * until it is ticked; the server refuses a checkout without the waiver
 * anyway, this window only collects it.
 */

export interface ConsentModalOptions {
  offer: ShopOffer;
  /** The box was ticked and « Payer » pressed. */
  onConfirm: () => void;
  onCancel: () => void;
}

export class ConsentModal {
  private readonly root: HTMLElement;
  private settled = false;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.finish(false);
  };

  constructor(private readonly options: ConsentModalOptions) {
    this.root = document.createElement('div');
    this.root.className = 'shop-modal';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', uiText('Confirmer l’achat'));
    // A click on the veil around the window cancels, like Escape.
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.finish(false);
    });

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window';
    const title = document.createElement('h2');
    title.className = 'shop-modal__title';
    title.textContent = uiText('Confirmer l’achat');
    const lead = document.createElement('p');
    lead.className = 'shop-modal__lead';
    lead.textContent = uiFormat('{offer} : {price}', {
      offer: options.offer.name,
      price: uiPrice(options.offer.priceCents),
    });

    const consent = document.createElement('label');
    consent.className = 'shop-modal__consent';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'shop-modal__check';
    const text = document.createElement('span');
    text.className = 'shop-modal__consent-text';
    text.textContent = uiText(
      'Je demande la livraison immédiate et je renonce à mon droit de rétractation.',
    );
    consent.append(box, text);

    const terms = document.createElement('p');
    terms.className = 'shop-modal__hint';
    const link = document.createElement('a');
    link.className = 'shop-modal__link';
    link.href = legalUrl('terms-of-sale');
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = uiText('Conditions générales de vente');
    terms.append(uiText('L’objet arrive dans ton sac dès le paiement confirmé. '), link, '.');

    const actions = document.createElement('div');
    actions.className = 'shop-modal__actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'shop-modal__button';
    cancel.textContent = uiText('Annuler');
    cancel.addEventListener('click', () => {
      this.finish(false);
    });
    const pay = document.createElement('button');
    pay.type = 'button';
    pay.className = 'shop-modal__button';
    pay.textContent = uiText('Payer');
    pay.disabled = true;
    box.addEventListener('change', () => {
      pay.disabled = !box.checked;
    });
    pay.addEventListener('click', () => {
      if (box.checked) this.finish(true);
    });
    actions.append(cancel, pay);

    frame.append(
      title,
      lead,
      consent,
      terms,
      actions,
      closeButton('Annuler (Échap)', () => {
        this.finish(false);
      }),
    );
    this.root.append(frame);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown, true);
    box.focus();
  }

  private finish(confirmed: boolean): void {
    if (this.settled) return;
    this.settled = true;
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.root.remove();
    if (confirmed) this.options.onConfirm();
    else this.options.onCancel();
  }
}
